import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Button, Input } from '@artosera/ui';
import { useEcartsTresorerie, type Ecarts } from '../../api/tresorerie.js';
import { cleMetier } from '../../api/cles.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { EnregistrerReglementDialog } from './EnregistrerReglementDialog.js';
import {
  TresorerieCadre,
  Montant,
  aujourdhui,
  dateParis,
  DateJour,
  EtatRequete,
} from './commun.js';
import styles from './Tresorerie.module.css';
function Contenu() {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation('regie');
  const client = useQueryClient();
  const [au, setAu] = useState(aujourdhui);
  const q = useEcartsTresorerie(galerieSlug, au);
  const [encaissement, setEncaissement] = useState<Ecarts['encaissementsNonEnregistres'][number]>();
  const label = (c: string) => t(`tresorerie.${c}`);
  return (
    <>
      <Field htmlFor="ecarts-au" label={label('etatAu')}>
        <Input
          id="ecarts-au"
          type="date"
          required
          value={au}
          onChange={(e) => setAu(e.target.value)}
        />
      </Field>
      <EtatRequete requete={q} />
      {q.data && (
        <>
          <h2>{label('etatRapprochement')}</h2>
          {q.data.etats.map((e) => (
            <section key={e.compteId} className={styles.carte}>
              <h3>{e.compte}</h3>
              <p>
                {label('soldeCalcule')} <Montant cents={e.soldeCentimes} /> ·{' '}
                <DateJour date={e.soldeLe} />
              </p>
              {e.soldeBanque && (
                <p>
                  {label('dernierReleve')} <DateJour date={e.soldeBanque.date} /> ·{' '}
                  <Montant cents={e.soldeBanque.montantCentimes} />
                </p>
              )}
              {e.soldeCalculeAuReleve && (
                <p>
                  {label('soldeCalculeAuReleve')}{' '}
                  <Montant cents={e.soldeCalculeAuReleve.montantCentimes} />
                </p>
              )}
              {e.ecartSolde !== undefined && (
                <p>
                  {label('ecartBanque')} <Montant cents={e.ecartSolde} />
                </p>
              )}
              <p>
                {label('nonRapproche')} <Montant cents={e.nonRapprocheCentimes} />
              </p>
              <ul>
                {e.lignesNonRapprochees.map((l) => (
                  <li key={l.id}>
                    <DateJour date={l.dateOperation} /> · {l.libelle} ·{' '}
                    <Montant cents={l.montantCentimes} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
          <section>
            <h2>{label('lignesAnciennes')}</h2>
            <ul>
              {q.data.lignesAnciennes.map((l) => (
                <li key={l.id}>
                  <DateJour date={l.dateOperation} /> · {l.libelle} ·{' '}
                  <Montant cents={l.montantCentimes} />
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2>{label('piecesSansLigne')}</h2>
            <ul>
              {q.data.piecesSansLigne.map((p) => (
                <li key={`${p.type}:${p.id}`}>
                  {label(`typesPieces.${p.type}`)} · {p.numero} · {p.contactNom} ·{' '}
                  <Montant cents={p.montantCentimes} />
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2>{label('encaissementsNonEnregistres')}</h2>
            <ul>
              {q.data.encaissementsNonEnregistres.map((e) => (
                <li key={`${e.factureId}:${e.ligneId}`}>
                  <Link to={`/${galerieSlug}/ventes/${e.factureId}`}>{e.numero}</Link> ·{' '}
                  <Montant cents={e.montantCentimes} /> · <DateJour date={e.dateOperation} /> ·{' '}
                  {e.reference}
                  {galerieALaction(galerie, 'payments:write') && (
                    <Button onClick={() => setEncaissement(e)}>
                      {label('enregistrerReglement')}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2>{label('orphelins')}</h2>
            <ul>
              {q.data.orphelins.map((r) => (
                <li key={r.id}>
                  <Link to={`/${galerieSlug}/tresorerie/lignes`}>{label('lignes')}</Link>{' '}
                  {r.pieces.map((p) => (
                    <span key={`${p.type}:${p.id}`}>
                      {' '}
                      · {label(`typesPieces.${p.type}`)} · <Montant cents={p.montantCentimes} />
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </section>
          <section>
            <h2>{label('avecEcart')}</h2>
            <ul>
              {q.data.avecEcart.map((r) => (
                <li key={r.id}>
                  {r.ecart && (
                    <>
                      {label(`naturesEcart.${r.ecart.nature}`)} · {r.ecart.motif} ·{' '}
                      <Montant cents={r.ecart.montantCentimes} />
                    </>
                  )}
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
      {encaissement && galerieALaction(galerie, 'payments:write') && (
        <EnregistrerReglementDialog
          galerieSlug={galerieSlug}
          factureId={encaissement.factureId}
          langue={i18n.language === 'en' ? 'en' : 'fr'}
          initial={{
            montantCentimes: encaissement.montantCentimes,
            date: dateParis(encaissement.dateOperation),
            reference: encaissement.reference,
          }}
          onClose={() => {
            setEncaissement(undefined);
            void client.invalidateQueries({ queryKey: cleMetier(galerieSlug, 'tresorerie') });
          }}
        />
      )}
    </>
  );
}
export function EcartsPage() {
  return (
    <TresorerieCadre titre="ecarts">
      <Contenu />
    </TresorerieCadre>
  );
}
